/**
 * GroupList.js
 * Component for listing and managing groups
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Table, 
  Badge, 
  Alert,
  Spinner,
  Button,
  Form,
  InputGroup,
  Modal
} from 'react-bootstrap';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import { groupService, commonService, courseService } from '../../services/api';
import { countValidSubscriptions } from '../../helpers/subscriptionHelpers';
import CreateGroupModal from './CreateGroupModal';
import { getAcademicYearOptions } from '../../helpers/timeHelper';

const GroupList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    totalCount: 0,
    totalPages: 1,
    page: 1,
    limit: 20
  });
  
  // Leggi parametri URL per filtri preimpostati
  const urlCourseId = searchParams.get('courseId');
  const urlAcademicYear = searchParams.get('academicYear');
  const urlSearch = searchParams.get('search') || '';

  const initialAcademicYearParamRef = useRef(urlAcademicYear);
  const hasInitializedYearRef = useRef(false);

  const getInitialAcademicYear = (param) => {
    if (!param || param === 'all') {
      return undefined;
    }
    const parsed = parseInt(param, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
  };

  // Filtri
  const [filters, setFilters] = useState({
    search: urlSearch,
    courseId: urlCourseId || null,
    academicYear: getInitialAcademicYear(urlAcademicYear)
  });

  // Filtro di ricerca locale (per il debounce)
  const [searchInput, setSearchInput] = useState(urlSearch);
  const searchTimeoutRef = useRef(null);
  // Pagina iniziale letta dall'URL (per ripristino al back)
  const initialPageRef = useRef(parseInt(searchParams.get('page') || '1', 10));

  // Corso selezionato per il filtro
  const [selectedCourse, setSelectedCourse] = useState(null);

  // Anno accademico corrente dal backend
  const [currentAcademicYear, setCurrentAcademicYear] = useState(null);

  // Modal per conferma eliminazione
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);

  // Modal per creazione/modifica
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  // Carica anno accademico corrente
  const loadCurrentAcademicYear = useCallback(async () => {
    try {
      const response = await commonService.getCurrentAcademicYear();
      if (response.success) {
        setCurrentAcademicYear(response.data);
        if (!hasInitializedYearRef.current) {
          hasInitializedYearRef.current = true;
          // Imposta automaticamente il filtro sull'anno corrente solo se non è stato fornito tramite URL
          if (initialAcademicYearParamRef.current === null) {
            setFilters(prev => ({
              ...prev,
              academicYear: response.data
            }));
          }
        }
      }
    } catch (err) {
      console.error('Error loading current academic year:', err);
      const fallbackYear = new Date().getMonth() >= 8 ? new Date().getFullYear() : new Date().getFullYear() - 1;
      setCurrentAcademicYear(fallbackYear);
      if (!hasInitializedYearRef.current) {
        hasInitializedYearRef.current = true;
        if (initialAcademicYearParamRef.current === null) {
          setFilters(prev => ({
            ...prev,
            academicYear: fallbackYear
          }));
        }
      }
    }
  }, []);

  // Carica lista gruppi
  const loadGroups = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
        search: filters.search || null,
        courseId: filters.courseId,
        academicYear: filters.academicYear,
        page,
        limit: pagination.limit
      };

      // Rimuovi parametri undefined prima di inviarli
      Object.keys(params).forEach(key => {
        if (params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await groupService.getGroups(params);
      
      if (response.success) {
        const data = response.data;
        setGroups(data.items || []);
        setPagination({
          totalCount: data.totalCount || 0,
          totalPages: data.totalPages || 1,
          page: data.page || 1,
          limit: data.limit || 20
        });
      } else {
        setError(response.message || 'Errore nel caricamento dei gruppi');
        setGroups([]);
      }
    } catch (err) {
      setError('Errore di connessione al server');
      setGroups([]);
      console.error('Error loading groups:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  // Effetti
  useEffect(() => {
    loadCurrentAcademicYear();
  }, [loadCurrentAcademicYear]);

  // Carica i dettagli del corso se fornito tramite URL
  useEffect(() => {
    const loadCourseFromUrl = async () => {
      if (urlCourseId && (!selectedCourse || selectedCourse.value !== urlCourseId)) {
        try {
          const response = await courseService.getCoursesSummary('');
          if (response.success) {
            const course = response.data.find(c => c.id === urlCourseId);
            if (course) {
              setSelectedCourse({
                value: course.id,
                label: `${course.name} (${course.academicYear}/${course.academicYear + 1})`,
                course: course
              });
            }
          }
        } catch (err) {
          console.error('Error loading course details:', err);
        }
      }
    };

    loadCourseFromUrl();
  }, [urlCourseId, selectedCourse]);

  useEffect(() => {
    // Carica gruppi quando cambiano i filtri o quando l'anno accademico corrente è disponibile
    if (currentAcademicYear !== null) {
      const pageToLoad = initialPageRef.current;
      initialPageRef.current = 1; // i successivi cambi di filtro ripartono da pagina 1
      loadGroups(pageToLoad);
    }
  }, [filters, loadGroups, currentAcademicYear]);

  // Sincronizza pagina e filtri nell'URL per ripristino al back
  useEffect(() => {
    // Non sincronizzare nell'URL finché l'anno accademico non è stato caricato
    if (currentAcademicYear === null) return;

    const params = {};
    if (filters.courseId) params.courseId = filters.courseId;
    if (filters.academicYear !== undefined) {
      params.academicYear = String(filters.academicYear);
    } else {
      params.academicYear = 'all';
    }
    if (filters.search) params.search = filters.search;
    if (pagination.page && pagination.page !== 1) params.page = String(pagination.page);
    setSearchParams(params, { replace: true });
  }, [pagination.page, filters, setSearchParams, currentAcademicYear]);

  // Cleanup del timeout quando il componente viene smontato
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Carica corsi per il filtro
  const loadCoursesAsync = async (inputValue) => {
    try {
      const response = await courseService.getCoursesSummary(inputValue || '');
      
      if (response.success) {
        let filteredCourses = response.data;
        
        // Filtra per anno accademico se selezionato
        if (filters.academicYear !== undefined) {
          filteredCourses = filteredCourses.filter(course => 
            course.academicYear === filters.academicYear
          );
        }
        
        return filteredCourses.map(course => ({
          value: course.id,
          label: `${course.name} (${course.academicYear}/${course.academicYear + 1})`,
          course: course
        }));
      }
      return [];
    } catch (err) {
      console.error('Error loading courses:', err);
      return [];
    }
  };

  const handleCourseChange = (selectedOption) => {
    setSelectedCourse(selectedOption);
    setFilters(prev => ({
      ...prev,
      courseId: selectedOption?.value || null
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Gestori eventi
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);

    // Cancella il timeout precedente
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Imposta un nuovo timeout per il debounce
    searchTimeoutRef.current = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        search: value
      }));
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500); // 500ms di debounce
  };

  const handleAcademicYearChange = (e) => {
    const value = e.target.value;
    
    // Reset corso selezionato quando cambia l'anno accademico
    setSelectedCourse(null);
    
    setFilters(prev => ({
      ...prev,
      academicYear: value === '' ? undefined : parseInt(value, 10),
      courseId: null // Reset anche il courseId nel filtro
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    loadGroups(newPage);
  };

  const confirmDelete = (group) => {
    setGroupToDelete(group);
    setShowDeleteModal(true);
  };

  const handleDeleteGroup = async () => {
    if (!groupToDelete) return;

    try {
      setLoading(true);
      const response = await groupService.deleteGroup(groupToDelete.id);
      
      if (response.success) {
        setShowDeleteModal(false);
        setGroupToDelete(null);
        // Ricarica la lista
        loadGroups(pagination.page);
      } else {
        setError(response.message || 'Errore nell\'eliminazione del gruppo');
      }
    } catch (err) {
      setError('Errore di connessione al server');
      console.error('Error deleting group:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setShowCreateModal(true);
  };

  const handleGroupCreated = () => {
    loadGroups(1); // Ricarica dalla prima pagina
  };

  const handleGroupUpdated = () => {
    loadGroups(pagination.page); // Ricarica pagina corrente
    setEditingGroup(null);
  };

  const handleModalClose = () => {
    setShowCreateModal(false);
    setEditingGroup(null);
  };

  const handleRollCall = (group) => {
    navigate(`/groups/${group.id}/rollcall`);
  };

  const getActiveSubscriptionsCount = (group) => {
    return countValidSubscriptions(group.courseSubscriptions || []);
  };

  const getParticipantsBadgeVariant = (group) => {
    const validCount = getActiveSubscriptionsCount(group);
    const maxParticipants = group.maxParticipants;
    
    if (validCount === 0) {
      return 'secondary';
    }
    
    if (maxParticipants) {
      const percentage = (validCount / maxParticipants) * 100;
      if (percentage >= 100) {
        return 'danger';
      } else if (percentage >= 80) {
        return 'warning';
      }
    }
    
    return 'primary';
  };

  if (loading && !groups.length) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Caricamento gruppi...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="group-list">
      <Row className="mb-4">
        <Col>
          <h2>Gestione Gruppi</h2>
        </Col>
        <Col xs="auto">
          <Button
            variant="primary"
            onClick={() => setShowCreateModal(true)}
          >
            <i className="fas fa-plus me-2"></i>
            Nuovo Gruppo
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {/* Filtri */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Cerca gruppi</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <i className="fas fa-search"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Cerca per nome gruppo..."
                    value={searchInput}
                    onChange={handleSearchChange}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Filtra per Corso</Form.Label>
                <AsyncSelect
                  key={filters.academicYear || 'all'} // Forza re-render quando cambia l'anno
                  cacheOptions
                  defaultOptions
                  loadOptions={loadCoursesAsync}
                  onChange={handleCourseChange}
                  value={selectedCourse}
                  placeholder="Seleziona un corso..."
                  noOptionsMessage={() => "Nessun corso trovato"}
                  loadingMessage={() => "Caricamento corsi..."}
                  isClearable
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: '38px'
                    })
                  }}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="academicYearSelect">
                <Form.Label>Anno Sportivo</Form.Label>
                <Form.Select
                  value={filters.academicYear || ''}
                  onChange={handleAcademicYearChange}
                >
                  <option value="">Tutti gli anni</option>
                  {getAcademicYearOptions(currentAcademicYear).map(year => (
                    <option key={year} value={year}>
                      {year}/{year + 1}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Lista gruppi */}
      <Card>
        <Card.Body className="p-0">
          {groups.length === 0 ? (
            <div className="text-center py-5">
              <i className="fas fa-users fa-3x text-muted mb-3"></i>
              <p className="text-muted mb-0">Nessun gruppo trovato</p>
            </div>
          ) : (
            <Table responsive className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th>Nome Gruppo</th>
                  <th>Corso</th>
                  <th>Anno Sportivo</th>
                  <th>Partecipanti</th>
                  <th>Descrizione</th>
                  <th className="text-center">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => (
                  <tr key={group.id}>
                    <td>
                      <strong 
                        className="text-primary text-decoration-none cursor-pointer"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleEditGroup(group)}
                        title="Clicca per modificare il gruppo"
                      >
                        {group.name}
                      </strong>
                    </td>
                    <td>
                      {group.course ? (
                        <span 
                          className="text-primary text-decoration-none cursor-pointer"
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            const courseOption = {
                              value: group.course.id,
                              label: `${group.course.name} (${group.course.academicYear}/${group.course.academicYear + 1})`,
                              course: group.course
                            };
                            handleCourseChange(courseOption);
                          }}
                          title="Clicca per filtrare i gruppi di questo corso"
                        >
                          {group.course.name}
                        </span>
                      ) : '-'}
                    </td>
                    <td>
                      {group.course ? `${group.course.academicYear}/${group.course.academicYear + 1}` : '-'}
                    </td>
                    <td>
                      <Link 
                        to={`/groups/${group.id}/participants`}
                        className="text-primary text-decoration-none fw-medium d-inline-flex align-items-center gap-1"
                        title="Clicca per vedere i partecipanti"
                      >
                        <i className="fas fa-users me-1"></i>
                        <span>Partecipanti</span>
                        <Badge bg={getParticipantsBadgeVariant(group)} pill className="ms-1">
                          {getActiveSubscriptionsCount(group)}{group.maxParticipants ? `/${group.maxParticipants}` : ''}
                        </Badge>
                        <i className="fas fa-chevron-right ms-1 text-muted" style={{ fontSize: '0.7rem' }}></i>
                      </Link>
                    </td>
                    <td>
                      {group.description ? (
                        <span title={group.description}>
                          {group.description.length > 50 ? 
                            `${group.description.substring(0, 50)}...` : 
                            group.description
                          }
                        </span>
                      ) : '-'}
                    </td>
                    <td className="text-center">
                      <div className="d-flex gap-2 justify-content-center">
                        <Button
                          variant="outline-info"
                          size="sm"
                          className="btn-action"
                          onClick={() => handleRollCall(group)}
                          title="Appello"
                        >
                          <i className="fas fa-eye"></i>
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="btn-action"
                          onClick={() => handleEditGroup(group)}
                          title="Modifica gruppo"
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="btn-action"
                          onClick={() => confirmDelete(group)}
                          disabled={getActiveSubscriptionsCount(group) > 0}
                          title={getActiveSubscriptionsCount(group) > 0 ? 
                            "Impossibile eliminare: gruppo con iscrizioni attive" : 
                            "Elimina gruppo"
                          }
                        >
                          <i className="fas fa-trash-alt"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Paginazione */}
      {pagination.totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <small className="text-muted">
            Pagina {pagination.page} di {pagination.totalPages} ({pagination.totalCount} elementi totali)
          </small>
          
          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1 || loading}
            >
              <i className="fas fa-chevron-left"></i>
            </Button>
            
            <span className="align-self-center mx-2">
              {pagination.page} / {pagination.totalPages}
            </span>
            
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages || loading}
            >
              <i className="fas fa-chevron-right"></i>
            </Button>
          </div>
        </div>
      )}

      {/* Modal per creazione/modifica gruppo */}
      <CreateGroupModal
        show={showCreateModal}
        onHide={handleModalClose}
        editingGroup={editingGroup}
        onGroupCreated={handleGroupCreated}
        onGroupUpdated={handleGroupUpdated}
      />

      {/* Modal conferma eliminazione */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Conferma Eliminazione</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {groupToDelete && (
            <>
              Sei sicuro di voler eliminare il gruppo <strong>"{groupToDelete.name}"</strong>?
              <br />
              <small className="text-muted">
                Questa azione non può essere annullata.
              </small>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowDeleteModal(false)}
            disabled={loading}
          >
            Annulla
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteGroup}
            disabled={loading}
          >
            {loading && <Spinner animation="border" size="sm" className="me-2" />}
            Elimina
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GroupList;